import { useTranslation } from 'next-i18next'
import React, { useEffect, useMemo, useState } from 'react'
import ModelsList from './components/ModelsList'
import AdjustSetting from './components/AdjustSetting'
import { useDispatch, useSelector } from 'react-redux'
import { runDeploymentService } from 'services/deployment.service'
import { removeImageService, uploadFileService, uploadMaskService } from 'services/file.service'
import { toggleAuthModal } from 'store/actions/header.action'
import { Loading } from 'components/index.js'
import { SelectMediaModal } from 'components/Modals'
import EditorModalHeader from './components/EditorModalHeader'
import ModelResultViewNew from './components/ModelResultView'
import EditorModalHeaderDesktop from './components/EditorModalHeaderDesktop'
import useIsMobile from 'hooks/useIsMobile'
import { generateFinalPromptStr, isValidUrl } from 'utils/formatUtils'
import { getMediaType } from 'utils/mediaUtils'
import { fetchtextToImagePrompts } from 'store/actions/prompt.action'
import { Button } from '@components/ui/button'
import { Search, Sparkles } from 'lucide-react'
import { Input } from '@components/ui/input'

const Editor = ({ selectedMedia, tags, goNext, goBack, generate, setGenerate, defaultSelectedModel }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('common')
  const isMobile = useIsMobile()

  const user = useSelector((state) => state.auth.user)
  const { data: models, loading } = useSelector((state) => state.models)
  const { textToImagePrompts } = useSelector((state) => state.prompt)

  const [activeMedia, setActiveMedia] = useState({
    img: selectedMedia,
    step: 0,
  })
  const [oldActiveMedia, setOldActiveMedia] = useState(null)
  const [selectedAI, setSelectedAI] = useState(null)
  const [selectMediaModalVisible, setSelectMediaModalVisible] = useState(false)
  const [enableSketch, setEnableSketch] = useState(false)
  const [historyList, setHistoryList] = useState([])
  const [loadingEnabled, setLoadingEnabled] = useState(false)
  const [emptyFieldsExist, setEmptyFieldsExist] = useState([])
  const [searchUpdate, setSearchUpdate] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [imagePrompt, setImagePrompt] = useState(generate || '')
  const [aspectRatio, setAspectRatio] = useState('1:1')
  const [imagesToGenerate, setImagesToGenerate] = useState(2)
  const [selectedParameters, setSelectedParameters] = useState({})
  const [generationData, setGenerationData] = useState([])
  const [modelListView, setModelListView] = useState('grid')
  const [isDeploying, setIsDeploying] = useState(false) // state to handle deployment loading

  const filteredModelsList = useMemo(() => {
    const normalizedSearchTerm = searchTerm?.toLowerCase()
    if (activeMedia)
      return searchTerm.length
        ? models?.filter(
            (model) =>
              model?.name?.toLowerCase().includes(normalizedSearchTerm) ||
              model?.description?.toLowerCase().includes(normalizedSearchTerm)
          )
        : models
  }, [models, searchTerm])

  useEffect(() => {
    document.addEventListener('keydown', downHandler)
    if (user) {
      if (!textToImagePrompts) dispatch(fetchtextToImagePrompts())
      console.log('fetchtextToImagePrompts called')
    }
    return () => {
      document.removeEventListener('keydown', downHandler)
    }
  }, [])

  useEffect(() => {
    let model
    if (models && !selectedAI && generate) {
      model = models?.find((item) => item.alias === defaultSelectedModel)
      let tempModel = structuredClone(model)
      if (tempModel) {
        tempModel = setRequiredArgs(defaultSelectedModel, tempModel, generate)
        setDefaultValues(tempModel)
        setSearchUpdate(true)
      }
    }
  }, [generate])

  useEffect(() => {
    if (searchUpdate) {
      runDeployment()
      setSearchUpdate(false)
      setGenerate('')
    }
  }, [searchUpdate])

  useEffect(() => {
    if (!enableSketch) {
      setActiveMedia({ img: selectedMedia, step: 0 })
      addHistory(selectedMedia)
    }
    if (oldActiveMedia) setOldActiveMedia(null)
  }, [selectedMedia])

  useEffect(() => {
    if (selectedAI) {
      const argsArray = Object.keys(selectedAI?.args)
      const emptyFieldData = argsArray?.filter(
        (item) =>
          selectedAI?.args[item]?.optional === false &&
          (selectedAI?.args[item].value === '' || !selectedAI?.args[item].value || selectedAI?.args[item].invalid)
      )
      setEmptyFieldsExist(emptyFieldData)
    }
  }, [selectedAI])

  const handleSelectedOptionChange = (parameterType, item) => {
    setSelectedParameters((prev) => ({
      ...prev,
      [parameterType]: prev[parameterType]?.title === item.title ? null : item,
    }))
  }
  const setRequiredArgs = (selectedAIModel, tempModel, prompt) => {
    let args
    switch (selectedAIModel) {
      case 'styley-text-to-image':
        args = {
          Prompt: { value: prompt },
          'Aspect Ratio': { value: '4:3' },
          'Magic Prompt': { value: 'ON' },
          'Style Type': { value: 'RENDER_3D' },
        }
        break
      case 'text-to-video':
        args = {
          prompt: { value: prompt },
          aspect_ratio: { value: '1:1' },
          loop: { value: 'true' },
        }
        break
      case 'text-to-speech':
        args = {
          Prompt: { value: prompt },
          Voice: { value: 'Eric-American-Male' },
        }
        break
      case 'text-to-3d':
        args = {
          'Negative Prompt': { value: 'low quality, low resolution, low poly, ugly' },
          'Output Format': { value: 'glb' },
          Prompt: { value: prompt },
          Style: { value: 'realistic' },
        }
    }
    tempModel.args = args
    return tempModel
  }

  const getArgsObj = (selectedAIModel) => {
    const detailedPrompt = generateFinalPromptStr(selectedParameters, imagePrompt)
    switch (selectedAIModel) {
      case 'styley-text-to-image':
        return {
          Prompt: { value: detailedPrompt },
          'Aspect Ratio': { value: '4:3' },
          'Magic Prompt': { value: 'ON' },
          'Style Type': { value: 'RENDER_3D' },
        }
      case 'text-to-video':
        return {
          prompt: { value: detailedPrompt },
          aspect_ratio: { value: '1:1' },
          loop: { value: 'true' },
        }
      case 'text-to-speech':
        return {
          Prompt: { value: detailedPrompt },
          Voice: { value: 'Eric-American-Male' },
        }
      case 'text-to-3d':
        return {
          'Negative Prompt': { value: 'low quality, low resolution, low poly, ugly' },
          'Output Format': { value: 'glb' },
          Prompt: { value: detailedPrompt },
          Style: { value: 'realistic' },
        }
    }
  }

  const handleImageGeneration = (selectedAIModel = 'text-to-image-sd-1.5') => {
    let model
    if (models) {
      model = models?.find((item) => item.alias === selectedAIModel)
      if (imagePrompt || generate) {
        let tempModel = structuredClone(model)
        const argsWithValue = getArgsObj(selectedAIModel)
        tempModel.args = { ...tempModel?.args, ...argsWithValue }
        setDefaultValues(tempModel)
        setSearchUpdate(true)
      }
    }
  }

  const handleDragStart = (e) => {
    e.dataTransfer.setData(
      'text/plain',
      JSON.stringify({
        url: activeMedia?.img,
        type: 'image',
      })
    )
  }

  const addDraggedImageToModel = async (url, item, single = true) => {
    let tempModel = structuredClone(selectedAI)
    setLoadingEnabled(true)
    if (url) {
      if (tempModel.args[item].value?.length && !single)
        tempModel.args[item].value = tempModel.args[item].value + ',' + url
      else tempModel.args[item].value = url
    }
    setLoadingEnabled(false)
    setSelectedAI(tempModel)
  }

  const setDefaultValues = (model, argsContainer = null) => {
    let tempModel = structuredClone(model)
    if (!tempModel) return
    Object.keys(tempModel.args).map((item) => {
      if (argsContainer) tempModel.args[item].value = argsContainer?.args[item]
      else
        tempModel.args[item].value =
          tempModel.args[item]?.value || (tempModel.examples && tempModel.examples[0]?.args[item]) || ''
    })
    setSelectedAI(tempModel)
  }

  const updateArgValues = (item, value) => {
    let tempModel = structuredClone(selectedAI)
    tempModel.args[item].value = value
    if (tempModel.args[item].type === 'url') {
      tempModel.args[item].invalid = !isValidUrl(value)
    }
    setSelectedAI(tempModel)
    if (selectMediaModalVisible) setSelectMediaModalVisible(false)
  }

  const addHistory = (img) => {
    let tempHistory = structuredClone(historyList)
    tempHistory.push({ img })
    setHistoryList(tempHistory)
  }

  const downHandler = (e) => {
    if (e.keyCode === 39) goNext()
    else if (e.keyCode === 37) {
      goBack()
    }
  }

  const uploadFiles = async (files, item, single = false) => {
    dispatch(uploadFileService(selectedAI, setSelectedAI, setLoadingEnabled, files, item, single))
  }

  const removeImage = (itemKey, index = null) => {
    removeImageService(selectedAI, setSelectedAI, itemKey, index)
  }

  const uploadFilesMask = async (file, item) => {
    dispatch(uploadMaskService(selectedAI, setSelectedAI, setEmptyFieldsExist, file, item))
  }

  const runDeployment = async () => {
    try {
      if (!user) {
        return dispatch(toggleAuthModal('sign_in'))
      }

      if (!selectedAI) return

      const setMainMedia = (url) => {
        if (url) {
          setActiveMedia({ img: url, step: 0 })
        }
      }

      dispatch(
        runDeploymentService(
          selectedAI,
          null,
          t,
          setGenerationData,
          generationData,
          setMainMedia,
          () => {},
          (loadingStatus) => {
            setIsDeploying(loadingStatus)
          }
        )
      )
    } catch (err) {
      console.log(err)
    }
  }

  return (
    <div className="image-generation-model   overflow-y-auto custom-scrollbar-horizontal !h-full  px-1 md:px-0 md:mt-4">
      {(loading || loadingEnabled) && <Loading />}
      {isMobile && (
        <EditorModalHeader
          selectedParameters={selectedParameters}
          setSelectedParameters={setSelectedParameters}
          handleSelectedOptionChange={handleSelectedOptionChange}
          setAspectRatio={setAspectRatio}
          aspectRatio={aspectRatio}
          setImagesToGenerate={setImagesToGenerate}
          imagesToGenerate={imagesToGenerate}
          t={t}
          handleImageGeneration={handleImageGeneration}
          imagePrompt={imagePrompt}
          setImagePrompt={setImagePrompt}
          constructModalUrl={() => {}}
        />
      )}

      <div className={` flex gap-3 h-full  ${isMobile ? 'flex-col' : 'flex-row'}`}>
        <div className={`flex flex-col gap-3 py-3  ${isMobile ? 'w-full' : 'w-[60%]'}  h-full`}>
          {!isMobile && (
            <EditorModalHeaderDesktop
              selectedParameters={selectedParameters}
              setSelectedParameters={setSelectedParameters}
              handleSelectedOptionChange={handleSelectedOptionChange}
              setAspectRatio={setAspectRatio}
              aspectRatio={aspectRatio}
              setImagesToGenerate={setImagesToGenerate}
              imagesToGenerate={imagesToGenerate}
              t={t}
              handleImageGeneration={handleImageGeneration}
              imagePrompt={imagePrompt}
              setImagePrompt={setImagePrompt}
              setActiveMedia={setActiveMedia}
              constructModalUrl={() => {}}
              selectedAIModel={defaultSelectedModel}
            />
          )}
          <ModelResultViewNew
            handleDragStart={handleDragStart}
            selectedMedia={selectedMedia}
            activeMedia={activeMedia}
            isDeploying={isDeploying}
            setActiveMedia={setActiveMedia}
            generationData={generationData}
            defaultSelectedModel={defaultSelectedModel}
            t={t}
          />
        </div>

        <div className={`flex flex-col bg-gray-50 overflow-y-auto p-3 gap-3 ${isMobile ? 'w-full' : 'w-[40%]'} h-full`}>
          <div className="flex md:pb-0  flex-col gap-3 justify-between items-center mb-4 md:flex-row md:mb-0">
            {(!selectedAI || generate) && (
              <div className="relative mt-2 w-full ">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="w-5 h-5 text-gray-400" />
                </div>
                <Input
                  type="text"
                  placeholder={t('common.search-models')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="py-6 pr-24 pl-10 rounded-lg transition-all border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
          </div>
          <div className="md:px-2 md:py-3">
            <div className="hidden justify-between items-center mb-2 md:flex">
              <p className="font-bold text-[18px] text-[#171A1FFF]">{t('common.pick-model')}</p>
              <div className="md:w-[30%] w-full flex gap-4 items-center md:items-start justify-between md:justify-end text-sm font-semibold md:font-black">
                <div className="flex gap-3 cursor-pointer">
                  <img
                    className={`h-6 ${modelListView == 'list' ? 'opacity-100' : 'opacity-40'}`}
                    onClick={() => setModelListView('list')}
                    src={`/assets/common/dropdown-view.svg`}
                    alt="dropdown view"
                  />
                  <img
                    className={`h-6 ${modelListView == 'grid' ? 'opacity-100' : 'opacity-40'}`}
                    onClick={() => setModelListView('grid')}
                    src={`/assets/common/box-view.svg`}
                    alt="box view"
                  />
                </div>
              </div>
            </div>
            <div className="pb-3 mb-4 ai-models-list md:overflow-x-hidden md:overflow-y-auto custom-scrollbar-horizontal">
              {!selectedAI || generate ? (
                <ModelsList
                  models={filteredModelsList || models}
                  setSelectedAI={(item) => setSelectedAI(item)}
                  tags={tags}
                  mediaType={getMediaType(selectedMedia)}
                  view={modelListView}
                />
              ) : (
                <AdjustSetting
                  selectedAI={selectedAI}
                  undoSelectedAI={() => {
                    setSelectedAI(null)
                    if (enableSketch) setEnableSketch(!enableSketch)
                  }}
                  runDeployment={runDeployment}
                  selectedModel={selectedAI}
                  argsArray={Object.keys(selectedAI?.args)}
                  setSelectedModel={setSelectedAI}
                  setDefaultValues={setDefaultValues}
                  updateArgValues={updateArgValues}
                  setSelectMediaModalVisible={setSelectMediaModalVisible}
                  uploadFile={uploadFiles}
                  removeImage={removeImage}
                  uploadFilesMask={uploadFilesMask}
                  emptyFieldsExist={emptyFieldsExist}
                  addDraggedImageToModel={addDraggedImageToModel}
                />
              )}
            </div>
            <Button
              disabled={emptyFieldsExist?.length > 0 || isDeploying}
              onClick={runDeployment}
              className={`
        w-full 
        h-10 md:h-12 
        text-xs md:text-sm lg:text-base 
        px-4 md:px-6 md:py-3 
        hover:bg-gradient-to-r hover:from-pink-500 hover:via-purple-500 hover:to-blue-400 
        transition duration-300 ease-in-out 
        relative overflow-hiddenl py-3 rounded-lg bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white font-medium
        ${isDeploying ? 'opacity-75 cursor-not-allowed' : ''}
      `}
            >
              <span className="flex relative z-10 justify-center items-center w-full">
                {isDeploying ? (
                  t('common.generating')
                ) : (
                  <>
                    {t('common.generate')}
                    <Sparkles className="ml-2 w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6" />
                  </>
                )}
              </span>
            </Button>
          </div>
        </div>
      </div>

      {selectMediaModalVisible && (
        <SelectMediaModal
          onClose={() => setSelectMediaModalVisible(false)}
          selectMedia={(selected) => {
            updateArgValues(selectMediaModalVisible, selected.full_url)
            let tempModel = structuredClone(selectedAI)

            if (
              tempModel.args[selectMediaModalVisible].value?.length &&
              tempModel.args[selectMediaModalVisible].type === 'image_list'
            )
              tempModel.args[selectMediaModalVisible].value =
                tempModel.args[selectMediaModalVisible].value + ',' + selected.full_url
            else tempModel.args[selectMediaModalVisible].value = selected.full_url
            setSelectedAI(tempModel)
          }}
        />
      )}
    </div>
  )
}

export default Editor
